import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Globe2, Layers } from "lucide-react";
import { api, errMsg, fmt, money } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { UsageMeter } from "@/components/UsageMeter";
import { AddWebsiteDialog } from "@/components/AddWebsiteDialog";
import { toast } from "sonner";

const Stat = ({ label, value, testid, tone = "text-slate-900" }) => (
  <div className="border-l border-slate-200 pl-4 first:border-l-0 first:pl-0">
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${tone}`} data-testid={testid}>{value}</div>
  </div>
);

const PlatformTag = ({ p }) => (
  <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-slate-300 text-slate-600">
    {p === "ghl" ? "GoHighLevel · es." : "Emergent · /es/"}
  </span>
);

const STATE = { active: null, included_words_used: "Paused: included words used", no_active_plan: "Paused: no active plan", business_suspended: "Paused: suspended", business_closed: "Paused: closed" };

const SiteRow = ({ site, usage, i }) => (
  <Link to={`/sites/${site.id}`} data-testid={`site-row-${site.id}`} className="rise grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1.3fr_auto] gap-4 md:gap-8 items-center px-5 py-5 bg-white border border-slate-200 rounded-lg hover:border-slate-900 transition-colors duration-150 group" style={{ animationDelay: `${i * 60}ms` }}>
    <div className="min-w-0">
      <div className="font-display font-semibold text-slate-900 truncate">{site.name}</div>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        <span className="font-mono text-xs text-slate-500 truncate">{site.domain}</span>
        <PlatformTag p={site.platform} />
      </div>
      {STATE[site.translation_state] && <div className="text-xs text-amber-700 mt-1.5" data-testid={`site-state-${site.id}`}>{STATE[site.translation_state]}</div>}
    </div>
    <div className="grid grid-cols-3 gap-3 text-xs">
      <div><div className="text-slate-500">Pages</div><div className="font-mono text-slate-900 mt-0.5">{site.stats.pages}</div></div>
      <div><div className="text-slate-500">Reviewed</div><div className="font-mono text-slate-900 mt-0.5">{site.stats.reviewed}</div></div>
      <div><div className="text-slate-500">Check</div><div className={`font-mono mt-0.5 ${site.stats.needs_check ? "text-blue-700" : "text-slate-900"}`}>{site.stats.needs_check}</div></div>
    </div>
    <UsageMeter row={usage} testid={`site-usage-${site.id}`} />
    <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-slate-900 transition-colors duration-150 hidden md:block" />
  </Link>
);

export default function Websites() {
  const { session } = useSession();
  const [sites, setSites] = useState(null);
  const [usage, setUsage] = useState(null);
  const limits = session.tenant?.limits || {};
  const max = limits["morrtranslate.max_websites"];

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([api.get("/sites"), api.get("/usage")]);
      setSites(s.data);
      setUsage(u.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => { load(); }, [load, session.org_id]);

  const usageFor = (id) => usage?.sites?.find((r) => r.site_id === id);
  const atLimit = max !== "unlimited" && sites && sites.length >= Number(max || 0);

  return (
    <div className="space-y-10">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Websites</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Spanish websites</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-xl">Each website gets {fmt(limits["morrtranslate.included_words_per_site"] || 50000)} translated words a month. Showing pages never uses words.</p>
        </div>
        <AddWebsiteDialog atLimit={atLimit} max={max} onCreated={load} />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="usage-summary">
        <Stat label="Websites on plan" value={`${sites?.length ?? "–"} / ${max ?? "–"}`} testid="websites-count" />
        <Stat label="Month (UTC)" value={usage?.month || "–"} testid="usage-month" />
        <Stat label="Words this month" value={fmt((usage?.sites || []).reduce((a, r) => a + (r.used || 0), 0))} testid="usage-total-words" />
        <Stat label="Extra usage this month" value={money(usage?.extra_cents)} testid="usage-extra-total" tone={usage?.extra_cents ? "text-red-600" : "text-slate-900"} />
      </div>

      {sites === null ? (
        <div className="font-mono text-sm text-slate-500" data-testid="sites-loading">Loading websites…</div>
      ) : sites.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center bg-white" data-testid="sites-empty">
          <Globe2 className="h-8 w-8 mx-auto text-slate-400" />
          <div className="font-display font-semibold mt-3">No websites yet</div>
          <p className="text-sm text-slate-500 mt-1">Add an Emergent site or a GoHighLevel site to start its Spanish version.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="sites-list">
          <div className="flex items-center gap-2 text-xs text-slate-500"><Layers className="h-3.5 w-3.5" /> {sites.length} website{sites.length > 1 ? "s" : ""}</div>
          {sites.map((s, i) => <SiteRow key={s.id} site={s} usage={usageFor(s.id)} i={i} />)}
        </div>
      )}
    </div>
  );
}
